import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils';
import { getCurrentLang } from '../../i18n/i18n';
import { TaxBreakdownGroup, TaxUnresolvedReason } from '../../api/booking/pricePreviewApiTypes';

interface TaxBreakdownProps {
    /** Total after discount (gross) shown on the "total" line. */
    displayTotal: number;
    /**
     * The per-rate groups from the backend (#1353). One row per rate — a
     * booking with a 7% stay and a 19% add-on has two, which is the normal
     * case for accommodation, not an edge one (Aufteilungsgebot).
     */
    taxBreakdown?: TaxBreakdownGroup[];
    /** Printed instead of a tax line when the supply is exempt (§19, §4 UStG). */
    taxExemptionNote?: string | null;
    /** Set when the backend could not resolve VAT. The total is still correct. */
    taxUnresolved?: TaxUnresolvedReason | null;
    /** Σ VAT across the groups. */
    tax?: number | null;
    /** Σ taxable base across the groups. */
    net?: number | null;
}

const Row = ({ label, value, indent }: { label: string, value: string, indent?: boolean }) => (
    <div className={`flex justify-between text-sm ${indent ? 'pl-4' : 'mt-2'}`}>
        <span>{label}</span>
        <span className={'font-bold'}>{value}</span>
    </div>
);

/**
 * Total / VAT / net block for the price boxes.
 *
 * The rates and amounts are the backend's (#1203, #1353) — this component does
 * no money arithmetic. It used to: it took a single `taxRate` and divided the
 * total by `1 + rate`, which is why a 7% room's 19% cleaning add-on was taxed
 * at 7%. The rate now arrives per group, already rounded once per group at
 * document level (EN 16931 BG-23), so the rows here always sum to the total.
 */
function TaxBreakdown({
    displayTotal,
    taxBreakdown,
    taxExemptionNote,
    taxUnresolved,
    tax,
    net,
}: Readonly<TaxBreakdownProps>) {
    const { t } = useTranslation();
    const lang = getCurrentLang();
    const groups = taxBreakdown ?? [];

    const total = (
        <Row
            label={t('booking_details.reservation_summary.total')}
            value={formatCurrency(displayTotal, lang)}
        />
    );

    // A failure must never look like an absence: say the VAT could not be
    // worked out, rather than silently rendering a total with no tax line —
    // which is indistinguishable from a tenant who charges none.
    if (taxUnresolved) {
        return (
            <>
                {total}
                <div className={'text-sm pl-4 text-foreground/60'}>
                    {t('booking_details.reservation_summary.tax_unresolved')}
                </div>
            </>
        );
    }

    if (groups.length === 0) return total;

    // Exempt / zero-rated: the note replaces the tax line. Rendering
    // "MwSt. (0%): 0,00 €" next to it would be a statement about a tax that is
    // not owed, which is not the same as one that is owed and happens to be nil.
    const exemptOnly = groups.every((g) => g.vatAmount === 0);
    if (exemptOnly && taxExemptionNote) {
        return (
            <>
                {total}
                <div className={'text-sm pl-4 text-foreground/60'}>{taxExemptionNote}</div>
            </>
        );
    }

    return (
        <>
            {total}
            {groups.map((g) => (
                <Row
                    key={`${g.vatCategoryCode}-${g.ratePercent}`}
                    indent
                    label={t('booking_details.reservation_summary.tax', {
                        taxRate: g.ratePercent.toFixed(g.ratePercent % 1 === 0 ? 0 : 2),
                    })}
                    value={formatCurrency(g.vatAmount, lang)}
                />
            ))}
            <Row
                indent
                label={t('booking_details.reservation_summary.net')}
                value={formatCurrency(net ?? 0, lang)}
            />
            {/* Only meaningful once there is more than one rate — with a single
                group the "total VAT" row would just repeat the row above it. */}
            {groups.length > 1 && (
                <Row
                    indent
                    label={t('booking_details.reservation_summary.tax_total')}
                    value={formatCurrency(tax ?? 0, lang)}
                />
            )}
        </>
    );
}

export default TaxBreakdown;
